import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { useSEO, createOrganizationSchema, createWebPageSchema } from '../../utils/seo';

export default function ServicesPage() {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<number | null>(null);

  useSEO({
    title: 'Hizmetlerimiz - AXIOM Kalite Kontrol ve İnsan Kaynakları Danışmanlığı',
    description: 'AXIOM olarak kalite kontrol, insan kaynakları danışmanlığı, ISO standartları, süreç optimizasyonu ve daha fazla hizmet sunuyoruz.',
    keywords: 'kalite kontrol, insan kaynakları, danışmanlık, ISO standartları, hizmetler',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services`,
    schema: {
      '@graph': [
        createOrganizationSchema(),
        createWebPageSchema(
          'Hizmetlerimiz - AXIOM',
          'AXIOM olarak kalite kontrol, insan kaynakları danışmanlığı ve ISO standartları hizmetleri sunuyoruz.'
        )
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      title: t('service1_title'),
      description: t('service1_desc'),
      image: 'https://public.readdy.ai/ai/img_res/9c43a6c93ad1b95ef8007e1157b32799.jpg',
      features: [
        'Ürün kalite kontrolü',
        'Süreç denetimi',
        'Kalite standartları uygulaması',
        'Hata analizi ve raporlama'
      ],
      detailedContent: {
        overview: 'Kontrol, ayıklama, tamir ve rötuş hizmetlerimiz ile ürünlerinizin en yüksek kalite standartlarında olmasını sağlıyoruz. Deneyimli ekibimiz, her türlü kusuru tespit eder ve gerekli düzeltmeleri yapar.',
        process: [
          'Gelen ürünlerin detaylı kontrolü',
          'Kusurlu parçaların tespit ve ayıklama',
          'Tamir edilebilir parçaların onarımı',
          'Yüzey rötuş ve düzeltme işlemleri',
          'Final kalite kontrolü ve onay'
        ],
        benefits: [
          'Ürün kalitesinde %99.8 başarı oranı',
          'Hızlı ve güvenilir hizmet',
          'Maliyet tasarrufu',
          'ISO standartlarına uygunluk'
        ]
      }
    },
    {
      title: t('service2_title'),
      description: t('service2_desc'),
      image: 'https://readdy.ai/api/search-image?query=quality%20control%20specialist%20performing%20visual%20dimensional%20inspection%20measurement%20of%20automotive%20components%20with%20calipers%20gauges%20modern%20factory%20setting%20clean%20background%20professional%20lighting%20manufacturing%20quality%20assurance&width=600&height=440&seq=service2new&orientation=landscape',
      features: [
        'Görsel muayene',
        'Boyutsal ölçüm',
        'Fonksiyonel testler',
        'Kalite raporlama'
      ],
      detailedContent: {
        overview: 'Görsel ve ölçüsel kontrol hizmetlerimiz ile ürünlerinizin teknik şartnamelere tam uyumunu garanti ediyoruz. İleri teknoloji ölçüm cihazları ve uzman personelimizle hassas kontroller gerçekleştiriyoruz.',
        process: [
          'Görsel muayene ve yüzey kalite kontrolü',
          'Boyutsal ölçümler (kumpas, mikrometre, projektör)',
          'Tolerans kontrolü ve doğrulama',
          'Fonksiyonel testler',
          'Detaylı raporlama ve dokümantasyon'
        ],
        benefits: [
          'Mikron hassasiyetinde ölçüm',
          'Kalibrasyon sertifikalı cihazlar',
          'Detaylı ölçüm raporları',
          'Hızlı geri bildirim'
        ]
      }
    },
    {
      title: t('service3_title'),
      description: t('service3_desc'),
      image: 'https://readdy.ai/api/search-image?query=automotive%20firewall%20component%20security%20system%20CSL%20containment%20suppression%20leak%20testing%20equipment%20modern%20manufacturing%20facility%20clean%20workspace%20industrial%20quality%20control%20professional%20lighting&width=600&height=440&seq=service3new&orientation=landscape',
      features: [
        'CSL test sistemleri',
        'Sızıntı testi',
        'Güvenlik kontrolü',
        'Performans analizi'
      ],
      detailedContent: {
        overview: 'Firewall ve CSL (Containment, Suppression, Leak) test hizmetlerimiz ile araç güvenliğini en üst seviyede tutuyoruz. Otomotiv sektöründe kritik öneme sahip bu testleri en güncel ekipmanlarla gerçekleştiriyoruz.',
        process: [
          'CSL1: Containment (Sınırlama) testleri',
          'CSL2: Suppression (Bastırma) testleri',
          'CSL3: Leak (Sızıntı) testleri',
          'Firewall bütünlük kontrolü',
          'Performans değerlendirme ve raporlama'
        ],
        benefits: [
          'Araç güvenliğinde maksimum koruma',
          'OEM standartlarına uygunluk',
          'Detaylı test raporları',
          'Hızlı ve güvenilir sonuçlar'
        ]
      }
    },
    {
      title: t('service5_title'),
      description: t('service5_desc'),
      image: 'https://readdy.ai/api/search-image?query=automotive%20paint%20coating%20thickness%20measurement%20gauge%20inspector%20checking%20vehicle%20surface%20quality%20modern%20facility%20clean%20background%20professional%20quality%20control%20industrial%20environment%20natural%20lighting&width=600&height=440&seq=service5new&orientation=landscape',
      features: [
        'Boya kalınlığı ölçümü',
        'Yüzey kalite kontrolü',
        'Kaplama analizi',
        'Görsel değerlendirme'
      ],
      detailedContent: {
        overview: 'Boya ve kaplama kalınlık kontrol hizmetlerimiz ile yüzey kalitesini garanti altına alıyoruz. Elektronik kalınlık ölçüm cihazları ile hassas ölçümler yaparak, boya kalitesini ve dayanıklılığını değerlendiriyoruz.',
        process: [
          'Elektronik kalınlık ölçüm cihazı ile ölçüm',
          'Çoklu nokta ölçüm ve haritalama',
          'Yüzey pürüzsüzlük kontrolü',
          'Renk tonu ve parlaklık kontrolü',
          'Detaylı kalınlık raporu hazırlama'
        ],
        benefits: [
          'Mikron hassasiyetinde ölçüm',
          'Korozyon koruması garantisi',
          'Estetik kalite kontrolü',
          'Uzun ömürlü yüzey kalitesi'
        ]
      }
    },
    {
      title: t('service6_title'),
      description: t('service6_desc'),
      image: 'https://readdy.ai/api/search-image?query=non-destructive%20testing%20NDT%20ultrasonic%20inspection%20equipment%20automotive%20parts%20quality%20control%20modern%20laboratory%20clean%20workspace%20professional%20technician%20industrial%20testing%20environment%20natural%20lighting&width=600&height=440&seq=service6new&orientation=landscape',
      features: [
        'Ultrasonik test',
        'Radyografik muayene',
        'Manyetik parçacık testi',
        'Penetrant testi'
      ],
      detailedContent: {
        overview: 'Tahribatlı ve tahribatsız muayene hizmetlerimiz ile malzeme bütünlüğünü ve güvenilirliğini test ediyoruz. İleri teknoloji NDT (Non-Destructive Testing) yöntemleri ile parçalarınızın iç yapısını zarar vermeden kontrol ediyoruz.',
        process: [
          'Ultrasonik test (UT) - iç kusur tespiti',
          'Radyografik muayene (RT) - yapısal analiz',
          'Manyetik parçacık testi (MT) - yüzey çatlak tespiti',
          'Penetrant testi (PT) - yüzey kusur kontrolü',
          'Detaylı muayene raporu ve sertifikasyon'
        ],
        benefits: [
          'Parçaya zarar vermeden kontrol',
          'İç kusurların tespiti',
          'Güvenlik ve dayanıklılık garantisi',
          'Uluslararası standartlara uygunluk'
        ]
      }
    },
    {
      title: 'Sorting Hizmetleri',
      description: 'Ürün ayıklama, sınıflandırma ve kalite bazlı ayrıştırma hizmetleri',
      image: 'https://readdy.ai/api/search-image?query=automotive%20parts%20sorting%20classification%20quality%20inspection%20worker%20organizing%20components%20on%20conveyor%20belt%20modern%20manufacturing%20facility%20clean%20workspace%20professional%20industrial%20environment%20natural%20lighting%20quality%20control%20process&width=600&height=440&seq=service7sorting&orientation=landscape',
      features: [
        'Ürün sınıflandırma',
        'Kalite bazlı ayırma',
        'Hatalı parça tespiti',
        'Envanter yönetimi'
      ],
      detailedContent: {
        overview: 'Sorting (ayıklama) hizmetlerimiz ile ürünlerinizi kalite kriterlerine göre sınıflandırıyor, hatalı parçaları ayırıyor ve envanter yönetiminizi optimize ediyoruz. Hızlı ve doğru sorting işlemleri ile üretim verimliliğinizi artırıyoruz.',
        process: [
          'Gelen ürünlerin detaylı incelenmesi',
          'Kalite kriterlerine göre sınıflandırma',
          'Hatalı ve kusurlu parçaların ayrıştırılması',
          'Onarılabilir parçaların belirlenmesi',
          'Envanter kayıt ve raporlama'
        ],
        benefits: [
          'Hızlı ve doğru ayıklama',
          'Üretim verimliliğinde artış',
          'Maliyet optimizasyonu',
          'Detaylı envanter takibi'
        ]
      }
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <header>
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'}`}>
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-3">
                <img src="https://static.readdy.ai/image/d71986f07c85c7b2b8264ab25e46d42e/edcc2fca5481151c158722794d786f43.png" alt="AXIOM Logo" className="h-12 w-12 object-contain" />
                <h1 className="text-xl font-bold text-gray-900">AXIOM</h1>
              </Link>
              
              {/* Desktop Menu */}
              <div className="hidden md:flex items-center gap-8">
                <Link to="/" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">{t('nav_home')}</Link>
                <Link to="/about" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">{t('nav_about')}</Link>
                <Link to="/services" className="text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors">{t('nav_services')}</Link>
                <Link to="/training" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">{t('nav_training')}</Link>
                <Link to="/blog" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">{t('nav_blog')}</Link>
                <Link to="/career" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">Kariyer</Link>
                <Link to="/contact" className="bg-teal-600 text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">{t('nav_contact')}</Link>
                <LanguageSwitcher />
              </div>

              {/* Mobile Menu Button */}
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden w-10 h-10 flex items-center justify-center text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
              <div className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
                <div className="flex flex-col gap-2">
                  <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_home')}</Link>
                  <Link to="/about" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_about')}</Link>
                  <Link to="/services" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_services')}</Link>
                  <Link to="/training" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_training')}</Link>
                  <Link to="/blog" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_blog')}</Link>
                  <Link to="/career" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">Kariyer</Link>
                  <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="bg-teal-600 text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-teal-700 transition-colors text-center whitespace-nowrap cursor-pointer">{t('nav_contact')}</Link>
                  <div className="px-3 py-2">
                    <LanguageSwitcher />
                  </div>
                </div>
              </div>
            )}
          </div>
        </nav>
      </header>

      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-24 bg-gradient-to-br from-teal-50 via-white to-emerald-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-teal-100 rounded-full px-4 py-2 mb-6">
                <i className="ri-service-line text-teal-600 text-sm"></i>
                <span className="text-teal-700 text-sm font-semibold">Hizmetlerimiz</span>
              </div>
              <h1 className="text-6xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight">
                {t('services_title')}
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                {t('services_subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {services.map((service, index) => (
                <div 
                  key={index} 
                  className="group cursor-pointer"
                  onClick={() => setSelectedService(index)}
                >
                  <div className="rounded-2xl overflow-hidden mb-6 h-80 bg-gray-100">
                    <img 
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4 group-hover:text-teal-600 transition-colors">{service.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                  <ul className="space-y-3">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-teal-600 text-sm"></i>
                        </div>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 flex items-center gap-2 text-teal-600 font-semibold group-hover:gap-3 transition-all">
                    <span>Detaylı Bilgi</span>
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">Hizmetlerimiz Hakkında Daha Fazla Bilgi Alın</h2>
            <p className="text-xl text-gray-600 mb-8">
              Kalite kontrol ve insan kaynakları ihtiyaçlarınız için size özel çözümler sunuyoruz. Detaylı bilgi almak için bizimle iletişime geçin.
            </p>
            <Link to="/contact" className="inline-flex items-center gap-3 bg-teal-600 text-white px-8 py-4 rounded-lg text-base font-semibold hover:bg-teal-700 transition-all hover:scale-105 whitespace-nowrap cursor-pointer">
              <span>İletişime Geçin</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
            <div className="lg:col-span-2">
              <Link to="/" className="flex items-center gap-3 mb-4">
                <img src="https://static.readdy.ai/image/d71986f07c85c7b2b8264ab25e46d42e/edcc2fca5481151c158722794d786f43.png" alt="AXIOM Logo" className="h-10 w-10 object-contain" />
                <span className="text-2xl font-bold">AXIOM</span>
              </Link>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                {t('footer_description')}
              </p>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/in/axiom-kalite" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
                <a href="https://x.com/AxiomKalite" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-twitter-x-fill text-lg"></i>
                </a>
                <a href="https://www.instagram.com/axiomkalite/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-lg"></i>
                </a>
                <a href="https://www.facebook.com/profile.php?id=61585433974202" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg mb-4">{t('footer_services')}</h4>
              <ul className="space-y-3">
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_quality')}</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_hr')}</Link></li>
                <li><Link to="/training" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_training')}</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_audit')}</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg mb-4">{t('footer_corporate')}</h4>
              <ul className="space-y-3">
                <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_about')}</Link></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_team')}</a></li>
                <li><Link to="/career" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_career')}</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_contact')}</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg mb-4">{t('footer_resources')}</h4>
              <ul className="space-y-3">
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_blog')}</Link></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_references')}</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_faq')}</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_documents')}</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-400">
              <p>{t('footer_copyright')}</p>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">
                {t('footer_powered')}
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Service Detail Modal */}
      {selectedService !== null && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedService(null)}
        >
          <div 
            className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 flex items-center justify-between z-10">
              <h2 className="text-3xl font-bold text-gray-900">{services[selectedService].title}</h2>
              <button 
                onClick={() => setSelectedService(null)}
                className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl text-gray-600"></i>
              </button>
            </div>

            <div className="p-8">
              {/* Image */}
              <div className="rounded-2xl overflow-hidden mb-8 h-96 bg-gray-100">
                <img 
                  src={services[selectedService].image}
                  alt={services[selectedService].title}
                  className="w-full h-full object-cover object-center"
                />
              </div>

              {/* Overview */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Hizmet Açıklaması</h3>
                <p className="text-gray-600 leading-relaxed text-lg">
                  {services[selectedService].detailedContent.overview}
                </p>
              </div>

              {/* Process */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Süreç Adımları</h3>
                <div className="space-y-4">
                  {services[selectedService].detailedContent.process.map((step, idx) => (
                    <div key={idx} className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                        {idx + 1}
                      </div>
                      <p className="text-gray-700 pt-1">{step}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Benefits */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Avantajlar</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {services[selectedService].detailedContent.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-teal-50 rounded-xl p-4">
                      <div className="w-6 h-6 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-800 font-medium">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8 text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Bu Hizmet İçin Teklif Alın</h3>
                <p className="text-gray-600 mb-6">Size özel çözümler sunmak için bizimle iletişime geçin</p>
                <Link 
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-700 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  <span>İletişime Geçin</span>
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}